import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { completePlaidUpdate, createPlaidLinkToken, exchangePlaidPublicToken } from './api';
// Client portal focuses on Plaid account connection and balance visibility
// Chart.js imports removed - investments functionality removed
import './App.css';
import { usePlaidLink } from 'react-plaid-link';
import {
  supabase,
  signUp,
  signIn,
  signOut,
  getSession,
  updatePassword,
  onAuthStateChange,
  validatePassword,
  enrollMFA,
  challengeMFA,
  verifyMFA,
  unenrollMFA,
  getMFAStatus,
} from './supabaseClient';
import Footer from './components/Footer';
import DataSecurityModal from './components/DataSecurityModal';
import SmsPreferences from './components/SmsPreferences';
import JobMatches from './components/JobMatches';
import MeetingStageHome from './components/MeetingStageHome';
import { isAccountSetupLink, isMeetingStageUser } from './lib/portalStage';

const PLAID_LINK_TOKEN_STORAGE_KEY = 'plaid_link_token';
const PLAID_RETURN_PATH_STORAGE_KEY = 'plaid_return_path';
const PLAID_RECONNECT_ITEM_ID_KEY = 'plaid_reconnect_item_id';

function PlaidOAuthReentryPage() {
  const [linkToken] = useState(() => localStorage.getItem(PLAID_LINK_TOKEN_STORAGE_KEY));
  const [oauthError, setOauthError] = useState('');

  const { open, ready } = usePlaidLink({
    token: linkToken,
    receivedRedirectUri: window.location.href,
    onSuccess: async (publicToken, metadata) => {
      try {
        const reconnectItemId = localStorage.getItem(PLAID_RECONNECT_ITEM_ID_KEY) || undefined;
        if (reconnectItemId) {
          await completePlaidUpdate(reconnectItemId);
        } else {
          await exchangePlaidPublicToken(publicToken, { metadata });
        }
        localStorage.removeItem(PLAID_LINK_TOKEN_STORAGE_KEY);
        localStorage.removeItem(PLAID_RECONNECT_ITEM_ID_KEY);
        const returnPath = localStorage.getItem(PLAID_RETURN_PATH_STORAGE_KEY) || '/';
        localStorage.removeItem(PLAID_RETURN_PATH_STORAGE_KEY);
        window.location.replace(`${returnPath}${returnPath.includes('?') ? '&' : '?'}plaid=connected`);
      } catch (error) {
        console.error('Plaid OAuth exchange error:', error);
        const duplicate = error?.response?.status === 409 || error?.response?.data?.duplicate;
        setOauthError(
          duplicate
            ? (error?.response?.data?.message ||
                'This account is already connected. Use Reconnect on the existing institution instead.')
            : 'Bank connection finished, but token exchange failed. Please return and try again.'
        );
        localStorage.removeItem(PLAID_RECONNECT_ITEM_ID_KEY);
      }
    },
    onExit: (error) => {
      if (error) {
        console.error('Plaid OAuth reentry exit error:', error);
        setOauthError(error.display_message || 'Bank connection was cancelled.');
      }
    }
  });

  useEffect(() => {
    if (ready && linkToken) {
      open();
    }
  }, [ready, linkToken, open]);

  return (
    <div className="app">
      <div className="login-container" style={{ textAlign: 'center' }}>
        <h2>Resuming Bank Connection</h2>
        {!linkToken && (
          <p>Missing Plaid session token. Return to dashboard and click "Connect Bank Account" again.</p>
        )}
        {linkToken && !oauthError && (
          <p>Finalizing your secure OAuth sign-in with your bank...</p>
        )}
        {oauthError && (
          <div className="auth-message error" style={{ marginTop: '12px' }}>
            {oauthError}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function App() {
  // Helper function for email validation (moved from supabaseClient.js as requested)
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const [step, setStep] = useState('login');
  // 'login', 'register', 'forgot-password'. The booking email's set-password link opens the code form directly.
  const [isAccountSetup] = useState(() => isAccountSetupLink());
  const [authMode, setAuthMode] = useState(() => {
    if (isAccountSetup) return 'forgot-password';
    // Calculator sign-up CTAs link to /portal?mode=register.
    return new URLSearchParams(window.location.search).get('mode') === 'register' ? 'register' : 'login';
  });
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [session, setSession] = useState(null);
  const [client, setClient] = useState(null);
  // Plaid linkToken removed
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [connectedAccountData, setConnectedAccountData] = useState({
    institutions: [],
    accounts: [],
    lastUpdated: null,
    fromCache: false
  });
  const [connectedAccountsLoaded, setConnectedAccountsLoaded] = useState(false);
  const [balancesRefreshing, setBalancesRefreshing] = useState(false);
  const [pendingPlaidPostConnect, setPendingPlaidPostConnect] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('plaid') === 'connected';
  });
  // Investments functionality removed
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  // True while a reset code is being checked: Supabase then reports PASSWORD_RECOVERY,
  // which must not switch the screen to the reset-link form.
  const codeResetInProgressRef = useRef(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [otpResetEmail, setOtpResetEmail] = useState('');
  const [otpResetCode, setOtpResetCode] = useState('');
  const [otpResetStage, setOtpResetStage] = useState('request'); // 'request' | 'verify'
  const [showDataSecurityModal, setShowDataSecurityModal] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [billingCanPortal, setBillingCanPortal] = useState(false);

  // MFA state
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState(null);
  const [mfaSecret, setMfaSecret] = useState(null);
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaPendingUser, setMfaPendingUser] = useState(null);

  // Auth form state
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });

  // Client portal focuses on Plaid account connection and balance visibility

  // Auto-logout after 15 minutes of inactivity with 1-minute warning
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!user || !session) {
      setShowInactivityWarning(false);
      return;
    }

    const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes
    const WARNING_TIME = 14 * 60 * 1000; // 14 minutes (1 minute before logout)
    let inactivityTimer;
    let warningTimer;

    const resetTimer = () => {
      // Clear existing timers
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }

      // Hide warning if user becomes active
      setShowInactivityWarning(false);

      // Set warning timer (14 minutes)
      warningTimer = setTimeout(() => {
        setShowInactivityWarning(true);
      }, WARNING_TIME);

      // Set logout timer (15 minutes)
      inactivityTimer = setTimeout(async () => {
        console.log('Auto-logout due to inactivity (15 minutes)');
        setShowInactivityWarning(false);
        try {
          await signOut();
          setUser(null);
          setSession(null);
          setClient(null);
          setStep('login');
          setAuthMode('login');
        } catch (error) {
          console.error('Auto-logout error:', error);
        }
      }, INACTIVITY_TIMEOUT);
    };

    // Activity events that reset the timer
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, resetTimer, true);
    });

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      if (warningTimer) {
        clearTimeout(warningTimer);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetTimer, true);
      });
      setShowInactivityWarning(false);
    };
  }, [user, session]);

  const loadClientProfile = useCallback(async (clientId) => {
    try {
      const response = await api.get(`/api/clients/${clientId}`);
      const apiClient = response.data?.client;
      if (apiClient) {
        setClient((prev) => ({
          ...(prev || {}),
          ...apiClient
        }));
      }
    } catch (error) {
      console.error('Error loading client profile:', error);
    }
  }, []);

  const loadBillingStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/billing/status');
      if (data?.success) {
        setBillingCanPortal(!!data.canUsePortal);
      } else {
        setBillingCanPortal(false);
      }
    } catch (error) {
      setBillingCanPortal(false);
    }
  }, []);

  const loadConnectedAccounts = useCallback(async (clientId) => {
    let institutions = [];
    try {
      const response = await api.get(`/api/clients/${clientId}/connected-accounts`);
      institutions = response.data?.institutions || [];
      setConnectedAccountData({
        institutions,
        accounts: response.data?.accounts || [],
        lastUpdated: response.data?.lastUpdated || null,
        fromCache: !!response.data?.fromCache
      });
    } catch (error) {
      console.error('Error loading connected accounts:', error);
      setConnectedAccountData({
        institutions: [],
        accounts: [],
        lastUpdated: null,
        fromCache: false
      });
    } finally {
      setConnectedAccountsLoaded(true);
    }

    if (institutions.length === 0) return;

    // Defer Plaid refresh so the dashboard (including SMS settings) is usable first.
    setTimeout(async () => {
      setBalancesRefreshing(true);
      try {
        const refresh = await api.post(`/api/clients/${clientId}/connected-accounts/refresh`);
        setConnectedAccountData({
          institutions: refresh.data?.institutions || institutions,
          accounts: refresh.data?.accounts || [],
          lastUpdated: refresh.data?.lastUpdated || null,
          fromCache: false
        });
      } catch (error) {
        console.error('Background balance refresh failed:', error);
      } finally {
        setBalancesRefreshing(false);
      }
    }, 2000);
  }, []);

  // Check session on mount
  useEffect(() => {
    let dataLoaded = false; // Prevent duplicate data loading
    
    const loadUserData = (userId) => {
      if (dataLoaded) return; // Skip if already loaded
      dataLoaded = true;
      console.log('Loading user data in background...');
      // Run all data fetching in parallel - don't await, let it load in background
      Promise.all([
        loadConnectedAccounts(userId),
        loadClientProfile(userId),
        loadBillingStatus()
      ]).then(() => {
        console.log('User data loaded successfully');
      }).catch(err => {
        console.error('Error loading user data:', err);
      });
    };

    // Set once a reset link is detected, so the session check below does not open
    // the dashboard before the user has chosen a new password.
    let recoveringPassword = false;

    const initAuth = async () => {
      try {
        // Check if this is a password reset flow
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        if (type === 'recovery' && accessToken) {
          // User clicked password reset link
          recoveringPassword = true;
          setIsPasswordReset(true);
          setStep('login');
          setAuthLoading(false);
          return;
        }

        const { data: { session } } = await getSession();
        if (recoveringPassword) return;
        if (session) {
          setSession(session);
          setUser(session.user);
          // Create client object from session
          const clientData = {
            clientId: session.user.id,
            name: session.user.user_metadata?.name || session.user.email,
            email: session.user.email
          };
          setClient(clientData);
          setConnectedAccountsLoaded(false);
          setStep('dashboard');
          setAuthLoading(false); // Show dashboard immediately
          // Load data in background (don't await)
          loadUserData(session.user.id);
        } else {
          setAuthLoading(false);
        }
      } catch (error) {
        console.error('Session check error:', error);
        setAuthLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes (handles fresh sign-ins and sign-outs)
    const { data: authListener } = onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event);
      if (event === 'PASSWORD_RECOVERY') {
        // Entering a reset code also reports this; that flow finishes on its own.
        if (codeResetInProgressRef.current) return;
        // Opened the link in a reset email: Supabase has signed the user in with a
        // recovery session. Ask for a new password before showing the dashboard.
        recoveringPassword = true;
        setIsPasswordReset(true);
        setStep('login');
        setAuthLoading(false);
        return;
      }
      if (event === 'SIGNED_IN' && session && !recoveringPassword) {
        setSession(session);
        setUser(session.user);
        const clientData = {
          clientId: session.user.id,
          name: session.user.user_metadata?.name || session.user.email,
          email: session.user.email
        };
        setClient(clientData);
        setConnectedAccountsLoaded(false);
        setStep('dashboard');
        setAuthLoading(false); // Show dashboard immediately

        // Load data in background (don't await)
        loadUserData(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        dataLoaded = false; // Reset flag on sign out
        setSession(null);
        setUser(null);
        setClient(null);
        setConnectedAccountData({ institutions: [], accounts: [], lastUpdated: null });
        setConnectedAccountsLoaded(false);
        setStep('login');
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [loadConnectedAccounts, loadClientProfile, loadBillingStatus, setAuthLoading, setIsPasswordReset, setStep, setSession, setUser, setClient]); // Cleaned up dependencies

  // Watch password for strength validation
  useEffect(() => {
    if (authForm.password) {
      const strength = validatePassword(authForm.password);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength({ isValid: false, errors: [] });
    }
  }, [authForm.password]);

  // Handle registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      // Validate inputs
      if (!authForm.firstName || !authForm.lastName || !authForm.email || !authForm.password || !authForm.confirmPassword) {
        setAuthError('All fields are required');
        setLoading(false);
        return;
      }

      if (!validateEmail(authForm.email)) {
        setAuthError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      if (!passwordStrength.isValid) {
        setAuthError(passwordStrength.errors.join('. '));
        setLoading(false);
        return;
      }

      if (authForm.password !== authForm.confirmPassword) {
        setAuthError('Passwords do not match');
        setLoading(false);
        return;
      }

      const fullName = `${authForm.firstName} ${authForm.lastName}`.trim();
      const { error } = await signUp(authForm.email, authForm.password, fullName);

      if (error) {
        if (error.message.includes('already registered')) {
          setAuthError('This email is already registered. Please login instead.');
        } else {
          setAuthError(error.message);
        }
        setLoading(false);
        return;
      }

      setAuthSuccess('Account created! Please check your email to verify your account. After you log in, click Connect Bank Account on the dashboard to securely link your accounts.');
      setAuthForm({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });

      // Switch to login mode after 5 seconds so they have time to read the message
      setTimeout(() => {
        setAuthMode('login');
        setAuthSuccess('');
      }, 5000);

    } catch (error) {
      console.error('Registration error:', error);
      setAuthError('Registration failed. Please try again.');
    }
    setLoading(false);
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      if (!authForm.email || !authForm.password) {
        setAuthError('Email and password are required');
        setLoading(false);
        return;
      }

      if (!validateEmail(authForm.email)) {
        setAuthError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      const { data, error } = await signIn(authForm.email, authForm.password);

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setAuthError('Invalid email or password');
        } else if (error.message.includes('Email not confirmed')) {
          setAuthError('Please verify your email first. Check your inbox for the verification link.');
        } else {
          setAuthError(error.message);
        }
        setLoading(false);
        return;
      }

      // Check if the user has MFA enabled
      const mfaStatus = await getMFAStatus();
      if (mfaStatus.nextLevel === 'aal2' && mfaStatus.factors.length > 0) {
        // User has MFA - need to verify before granting access
        const factor = mfaStatus.factors[0];
        const { data: challengeData, error: challengeError } = await challengeMFA(factor.id);

        if (challengeError) {
          setAuthError('Failed to initiate MFA challenge. Please try again.');
          setLoading(false);
          return;
        }

        // Store MFA state and show verification screen
        setMfaFactorId(factor.id);
        setMfaChallengeId(challengeData.id);
        setMfaPendingUser(data);
        setStep('mfa-verify');
        setLoading(false);
        return;
      }

      // No MFA - proceed directly to dashboard
      const clientData = {
        clientId: data.user.id,
        name: data.user.user_metadata?.name || data.user.email,
        email: data.user.email
      };

      setUser(data.user);
      setSession(data.session);
      setClient(clientData);
      setStep('dashboard');

      // Load dashboard data
      console.log('User logged in successfully - fetching user data...');
      setConnectedAccountsLoaded(false);
      await Promise.all([
        loadConnectedAccounts(data.user.id),
        loadClientProfile(data.user.id),
        loadBillingStatus()
      ]);

    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Login failed. Please try again.');
    }
    setLoading(false);
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      if (!authForm.email) {
        setAuthError('Email is required');
        setLoading(false);
        return;
      }

      if (!validateEmail(authForm.email)) {
        setAuthError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      // Moved resetPassword logic here to avoid importing from supabaseClient.js
      // Return to the portal's login page: '/' redirects to the marketing /links page,
      // where the portal (and its reset form) never loads.
      const { error } = await supabase.auth.resetPasswordForEmail(authForm.email, {
        redirectTo: `${window.location.origin}/login`
      });

      if (error) {
        setAuthError(error.message);
        setLoading(false);
        return;
      }

      setOtpResetEmail(authForm.email.trim().toLowerCase());
      setOtpResetCode('');
      setOtpResetStage('verify');
      setAuthSuccess('Check your email. If it has a 6-digit code, enter it below. If it has a link instead, open the link to set your new password.');
      setAuthForm({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });

    } catch (error) {
      console.error('Password reset error:', error);
      setAuthError('Password reset failed. Please try again.');
    }
    setLoading(false);
  };

  // Handle OTP-based password reset
  const handleOtpPasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      if (!otpResetEmail) {
        setAuthError('Reset email is missing. Please request a new reset code.');
        setLoading(false);
        return;
      }

      if (!otpResetCode || otpResetCode.trim().length < 6) {
        setAuthError('Please enter the reset code from your email.');
        setLoading(false);
        return;
      }

      if (!newPassword || !confirmNewPassword) {
        setAuthError('Both password fields are required');
        setLoading(false);
        return;
      }

      const strength = validatePassword(newPassword);
      if (!strength.isValid) {
        setAuthError(strength.errors.join('. '));
        setLoading(false);
        return;
      }

      if (newPassword !== confirmNewPassword) {
        setAuthError('Passwords do not match');
        setLoading(false);
        return;
      }

      codeResetInProgressRef.current = true;
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: otpResetEmail,
        token: otpResetCode.trim(),
        type: 'recovery'
      });

      if (verifyError) {
        setAuthError(verifyError.message || 'Invalid or expired reset code.');
        setLoading(false);
        return;
      }

      const { error } = await updatePassword(newPassword);
      if (error) {
        setAuthError(error.message || 'Failed to update password.');
        setLoading(false);
        return;
      }

      await signOut();
      setIsPasswordReset(false);
      setAuthSuccess('Password updated. Log in with your new password.');
      setOtpResetCode('');
      setOtpResetEmail('');
      setOtpResetStage('request');
      setNewPassword('');
      setConfirmNewPassword('');
      setAuthMode('login');
      setStep('login');
    } catch (error) {
      console.error('OTP password reset error:', error);
      setAuthError('Password reset failed. Please try again.');
    } finally {
      codeResetInProgressRef.current = false;
    }

    setLoading(false);
  };

  // Handle password reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthSuccess('');
    setLoading(true);

    try {
      if (!newPassword || !confirmNewPassword) {
        setAuthError('Both password fields are required');
        setLoading(false);
        return;
      }

      const strength = validatePassword(newPassword);
      if (!strength.isValid) {
        setAuthError(strength.errors.join('. '));
        setLoading(false);
        return;
      }

      if (newPassword !== confirmNewPassword) {
        setAuthError('Passwords do not match');
        setLoading(false);
        return;
      }

      const { error } = await updatePassword(newPassword);

      if (error) {
        setAuthError(error.message);
        setLoading(false);
        return;
      }

      setAuthSuccess('Password updated successfully! Redirecting to login...');
      setNewPassword('');
      setConfirmNewPassword('');
      setIsPasswordReset(false);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.hash = '';
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      setAuthError('Password reset failed. Please try again.');
    }
    setLoading(false);
  };

  // Handle MFA verification during login
  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      if (!mfaCode || mfaCode.length !== 6) {
        setAuthError('Please enter a valid 6-digit code');
        setLoading(false);
        return;
      }

      const { data: verifyData, error: verifyError } = await verifyMFA(
        mfaFactorId,
        mfaChallengeId,
        mfaCode
      );

      if (verifyError) {
        setAuthError('Invalid verification code. Please try again.');
        // Create a new challenge for retry
        const { data: newChallenge } = await challengeMFA(mfaFactorId);
        if (newChallenge) {
          setMfaChallengeId(newChallenge.id);
        }
        setMfaCode('');
        setLoading(false);
        return;
      }

      // MFA verified successfully - proceed to dashboard
      const pendingData = mfaPendingUser;
      const clientData = {
        clientId: pendingData.user.id,
        name: pendingData.user.user_metadata?.name || pendingData.user.email,
        email: pendingData.user.email
      };

      setUser(pendingData.user);
      setSession(verifyData.session || pendingData.session);
      setClient(clientData);
      setMfaPendingUser(null);
      setMfaFactorId(null);
      setMfaChallengeId(null);
      setMfaCode('');
      setStep('dashboard');

      console.log('MFA verified - fetching user data...');
      setConnectedAccountsLoaded(false);
      await Promise.all([
        loadConnectedAccounts(pendingData.user.id),
        loadClientProfile(pendingData.user.id),
        loadBillingStatus()
      ]);

    } catch (error) {
      console.error('MFA verification error:', error);
      setAuthError('Verification failed. Please try again.');
    }
    setLoading(false);
  };

  // Handle MFA enrollment - start
  const handleMfaEnroll = async () => {
    setAuthError('');
    setMfaEnrolling(true);

    try {
      const { data, error } = await enrollMFA('Authenticator App');

      if (error) {
        setAuthError('Failed to start MFA enrollment: ' + error.message);
        setMfaEnrolling(false);
        return;
      }

      // data contains: id (factorId), totp.qr_code (data URI), totp.secret, totp.uri
      setMfaFactorId(data.id);
      setMfaQrCode(data.totp.qr_code);
      setMfaSecret(data.totp.secret);
      setShowMfaSetup(true);

    } catch (error) {
      console.error('MFA enrollment error:', error);
      setAuthError('Failed to set up two-factor authentication.');
      setMfaEnrolling(false);
    }
  };

  // Handle MFA enrollment - verify the code to complete setup
  const handleMfaEnrollVerify = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      if (!mfaCode || mfaCode.length !== 6) {
        setAuthError('Please enter a valid 6-digit code');
        setLoading(false);
        return;
      }

      // Create a challenge and verify it to complete enrollment
      const { data: challengeData, error: challengeError } = await challengeMFA(mfaFactorId);

      if (challengeError) {
        setAuthError('Failed to create verification challenge: ' + challengeError.message);
        setLoading(false);
        return;
      }

      const { error: verifyError } = await verifyMFA(
        mfaFactorId,
        challengeData.id,
        mfaCode
      );

      if (verifyError) {
        setAuthError('Invalid code. Please check your authenticator app and try again.');
        setMfaCode('');
        setLoading(false);
        return;
      }

      // Enrollment successful
      setMfaEnabled(true);
      setShowMfaSetup(false);
      setMfaEnrolling(false);
      setMfaQrCode(null);
      setMfaSecret(null);
      setMfaCode('');
      setMfaFactorId(null);
      setAuthSuccess('Two-factor authentication has been enabled successfully!');

      setTimeout(() => setAuthSuccess(''), 5000);

    } catch (error) {
      console.error('MFA enrollment verify error:', error);
      setAuthError('Failed to verify code. Please try again.');
    }
    setLoading(false);
  };

  // Handle MFA disable
  const handleMfaDisable = async () => {
    setAuthError('');
    setLoading(true);

    try {
      const mfaStatus = await getMFAStatus();
      if (mfaStatus.factors.length > 0) {
        const { error } = await unenrollMFA(mfaStatus.factors[0].id);
        if (error) {
          setAuthError('Failed to disable two-factor authentication: ' + error.message);
          setLoading(false);
          return;
        }
      }

      setMfaEnabled(false);
      setAuthSuccess('Two-factor authentication has been disabled.');
      setTimeout(() => setAuthSuccess(''), 5000);

    } catch (error) {
      console.error('MFA disable error:', error);
      setAuthError('Failed to disable two-factor authentication.');
    }
    setLoading(false);
  };

  // Check MFA status when user is authenticated
  useEffect(() => {
    const checkMfaStatus = async () => {
      if (user && session) {
        const status = await getMFAStatus();
        setMfaEnabled(status.factors.length > 0);
      }
    };
    checkMfaStatus();
  }, [user, session]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      setUser(null);
      setSession(null);
      setClient(null);
      setStep('login');
      setAuthMode('login');
      setAuthForm({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '' });
      setConnectedAccountData({ institutions: [], accounts: [], lastUpdated: null });
      setConnectedAccountsLoaded(false);
      setBillingCanPortal(false);
      // Investments functionality removed
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      const { data } = await api.post('/api/billing/portal', {
        returnUrl: `${window.location.origin}${window.location.pathname || '/'}`
      });
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      window.alert(msg || 'Could not open billing portal');
    }
  };

  // Refresh connected-account balances from Plaid (updates cache)
  const refreshData = async () => {
    if (!client?.clientId) return;
    setLoading(true);
    setBalancesRefreshing(true);
    try {
      const refresh = await api.post(`/api/clients/${client.clientId}/connected-accounts/refresh`);
      setConnectedAccountData({
        institutions: refresh.data?.institutions || [],
        accounts: refresh.data?.accounts || [],
        lastUpdated: refresh.data?.lastUpdated || null,
        fromCache: false
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
      setBalancesRefreshing(false);
    }
  };

  // Plaid Link (bank connection)
  const [plaidLinkToken, setPlaidLinkToken] = useState(null);
  const [plaidLinkLoading, setPlaidLinkLoading] = useState(false);
  const [plaidLinkError, setPlaidLinkError] = useState('');
  const [openPlaidWhenReady, setOpenPlaidWhenReady] = useState(false);
  const plaidReconnectItemIdRef = useRef(null);

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: plaidLinkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        const isReconnect = Boolean(plaidReconnectItemIdRef.current);
        if (isReconnect) {
          await completePlaidUpdate(plaidReconnectItemIdRef.current);
        } else {
          await exchangePlaidPublicToken(publicToken, { metadata });
        }
        localStorage.removeItem(PLAID_LINK_TOKEN_STORAGE_KEY);
        localStorage.removeItem(PLAID_RETURN_PATH_STORAGE_KEY);
        localStorage.removeItem(PLAID_RECONNECT_ITEM_ID_KEY);
        await refreshData();
        if (client?.clientId) {
          await loadClientProfile(client.clientId);
        }
        setPlaidLinkToken(null);
        plaidReconnectItemIdRef.current = null;
      } catch (error) {
        console.error('Plaid exchange error:', error);
        const duplicate =
          error?.response?.status === 409 || error?.response?.data?.duplicate;
        setPlaidLinkError(
          duplicate
            ? (error?.response?.data?.message ||
                'This account is already connected. Use Reconnect on the existing institution instead.')
            : 'Failed to connect bank. Please try again.'
        );
        plaidReconnectItemIdRef.current = null;
      }
    },
    onExit: (error) => {
      localStorage.removeItem(PLAID_LINK_TOKEN_STORAGE_KEY);
      localStorage.removeItem(PLAID_RETURN_PATH_STORAGE_KEY);
      localStorage.removeItem(PLAID_RECONNECT_ITEM_ID_KEY);
      if (error) {
        console.error('Plaid exit error:', error);
        setPlaidLinkError(error.display_message || 'Plaid connection was cancelled.');
      }
      // Reset token on exit so repeated "Connect Bank Account" clicks always reinitialize Plaid Link.
      setPlaidLinkToken(null);
      plaidReconnectItemIdRef.current = null;
    }
  });

  useEffect(() => {
    if (openPlaidWhenReady && plaidReady) {
      openPlaid();
      setOpenPlaidWhenReady(false);
    }
  }, [openPlaidWhenReady, plaidReady, openPlaid]);

  const handleConnectAccount = async (itemId = null) => {
    setPlaidLinkError('');
    setPlaidLinkLoading(true);
    try {
      setPlaidLinkToken(null);
      plaidReconnectItemIdRef.current = itemId || null;
      const { link_token } = await createPlaidLinkToken(itemId ? { itemId } : {});
      if (!link_token) {
        throw new Error('No link token returned from server.');
      }
      localStorage.setItem(PLAID_LINK_TOKEN_STORAGE_KEY, link_token);
      localStorage.setItem(PLAID_RETURN_PATH_STORAGE_KEY, window.location.pathname || '/');
      if (itemId) {
        localStorage.setItem(PLAID_RECONNECT_ITEM_ID_KEY, itemId);
      } else {
        localStorage.removeItem(PLAID_RECONNECT_ITEM_ID_KEY);
      }
      setPlaidLinkToken(link_token);
      setOpenPlaidWhenReady(true);
    } catch (error) {
      console.error('Plaid link token error:', error);
      setPlaidLinkError(
        itemId
          ? 'Unable to start reconnect. Please try again.'
          : 'Unable to start bank connection. Please try again.'
      );
      plaidReconnectItemIdRef.current = null;
    } finally {
      setPlaidLinkLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatAccountType = (type, subtype) => {
    const label = subtype || type || 'Account';
    return label
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const formatLastUpdated = (value) => {
    if (!value) return null;
    return new Date(value).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const connectedInstitutions = connectedAccountData.institutions.length;
  const connectedAccounts = connectedAccountData.accounts.length;
  const institutionsNeedingReconnect = connectedAccountData.institutions.filter(
    (inst) => inst.needsReconnect || inst.connectionStatus === 'Action needed'
  );
  const institutionsNeedingInvestmentsConsent = connectedAccountData.institutions.filter(
    (inst) => inst.needsInvestmentsConsent
  );
  const institutionsWithExpiringConsent = institutionsNeedingReconnect.filter(
    (inst) => inst.plaidErrorCode === 'PENDING_DISCONNECT' || inst.pendingDisconnectAt
  );
  const institutionsNeedingLogin = institutionsNeedingReconnect.filter(
    (inst) => !inst.needsInvestmentsConsent &&
      inst.plaidErrorCode !== 'PENDING_DISCONNECT' &&
      !inst.pendingDisconnectAt
  );

  useEffect(() => {
    if (!pendingPlaidPostConnect || !client?.clientId || step !== 'dashboard') return;

    const runPostConnectRefresh = async () => {
      try {
        await refreshData();
      } finally {
        const params = new URLSearchParams(window.location.search);
        params.delete('plaid');
        const next = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${next ? `?${next}` : ''}`);
        setPendingPlaidPostConnect(false);
      }
    };

    runPostConnectRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPlaidPostConnect, client?.clientId, step]);

  if (window.location.pathname === '/oauth-page') {
    return <PlaidOAuthReentryPage />;
  }

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div className="app">
        <div className="login-container">
          <h1>Financial Progress Portal</h1>
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }


  // Login/Register/ForgotPassword/MFA Screen
  if (step === 'login' || step === 'mfa-verify') {
    return (
      <div className="app">
        <div className="login-container">
          <h1>Financial Progress Portal</h1>
          <p>
            {isAccountSetup
              ? 'Set a password to open your client portal.'
              : 'Connect your accounts so Bautista Planning and Analytics can prepare your reports.'}
          </p>

          {/* Password Reset Form */}
          {isPasswordReset && (
            <form onSubmit={handlePasswordReset} className="login-form">
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2D5074' }}>Set New Password</h2>

              {authError && (
                <div className="auth-message error">{authError}</div>
              )}
              {authSuccess && (
                <div className="auth-message success">{authSuccess}</div>
              )}

              <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '20px' }}>
                Enter your new password below.
              </p>

              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {newPassword && (
                <div className="password-strength">
                  {(() => {
                    const strength = validatePassword(newPassword);
                    return strength.isValid ? (
                      <div style={{ color: '#28a745' }}>✓ Strong password</div>
                    ) : (
                      <div style={{ color: '#dc3545', fontSize: '0.85em' }}>
                        {strength.errors.map((err, idx) => (
                          <div key={idx}>• {err}</div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm New Password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
                <div className="password-match-error">Passwords do not match</div>
              )}

              <button type="submit" disabled={loading}>
                {loading ? 'Updating Password...' : 'Update Password'}
              </button>
            </form>
          )}

          {/* Registration Form */}
          {!isPasswordReset && authMode === 'register' && (
            <form onSubmit={handleRegister} className="login-form">
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2D5074' }}>Create Account</h2>

              {authError && (
                <div className="auth-message error">{authError}</div>
              )}
              {authSuccess && (
                <div className="auth-message success">{authSuccess}</div>
              )}

              <input
                type="text"
                placeholder="First Name"
                value={authForm.firstName}
                onChange={(e) => setAuthForm({...authForm, firstName: e.target.value})}
                required
              />

              <input
                type="text"
                placeholder="Last Name"
                value={authForm.lastName}
                onChange={(e) => setAuthForm({...authForm, lastName: e.target.value})}
                required
              />

              <input
                type="email"
                placeholder="Email"
                value={authForm.email}
                onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                required
              />

              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {authForm.password && (
                <div className="password-strength">
                  {passwordStrength.isValid ? (
                    <div style={{ color: '#28a745' }}>✓ Strong password</div>
                  ) : (
                    <div style={{ color: '#dc3545', fontSize: '0.85em' }}>
                      {passwordStrength.errors.map((err, idx) => (
                        <div key={idx}>• {err}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={authForm.confirmPassword}
                  onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              {authForm.password && authForm.confirmPassword && authForm.password !== authForm.confirmPassword && (
                <div className="password-match-error">Passwords do not match</div>
              )}

              <button type="submit" disabled={loading || !passwordStrength.isValid}>
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>

              <div className="auth-toggle">
                Already have an account?{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setAuthMode('login');
                    setAuthError('');
                    setAuthSuccess('');
                  }}
                >
                  Login
                </button>
              </div>
            </form>
          )}

          {/* Login Form */}
          {!isPasswordReset && authMode === 'login' && step !== 'mfa-verify' && (
            <form onSubmit={handleLogin} className="login-form">
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2D5074' }}>Login</h2>

              {authError && (
                <div className="auth-message error">{authError}</div>
              )}
              {authSuccess && (
                <div className="auth-message success">{authSuccess}</div>
              )}

              <input
                type="email"
                placeholder="Email"
                value={authForm.email}
                onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                required
              />

              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={authForm.password}
                  onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>

              <button type="submit" disabled={loading} style={{ marginBottom: '15px' }}>
                {loading ? 'Logging in...' : 'Login'}
              </button>

              <div className="forgot-password-link" style={{ textAlign: 'center', marginTop: '0' }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setAuthMode('forgot-password');
                    setAuthError('');
                    setAuthSuccess('');
                    setOtpResetStage('request');
                    setOtpResetEmail('');
                    setOtpResetCode('');
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <div className="auth-toggle">
                Don't have an account?{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setAuthMode('register');
                    setAuthError('');
                    setAuthSuccess('');
                  }}
                >
                  Sign up
                </button>
              </div>
            </form>
          )}

          {/* MFA Verification Form (shown after password login) */}
          {!isPasswordReset && authMode === 'login' && step === 'mfa-verify' && (
            <form onSubmit={handleMfaVerify} className="login-form">
              <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#2D5074' }}>Two-Factor Authentication</h2>
              <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '20px' }}>
                Enter the 6-digit code from your authenticator app.
              </p>

              {authError && (
                <div className="auth-message error">{authError}</div>
              )}

              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: '#e3f2fd',
                  marginBottom: '15px'
                }}>
                  <span style={{ fontSize: '28px' }}>&#128274;</span>
                </div>
              </div>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setMfaCode(val);
                }}
                autoFocus
                style={{
                  textAlign: 'center',
                  fontSize: '24px',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  fontWeight: 'bold'
                }}
                required
              />

              <button type="submit" disabled={loading || mfaCode.length !== 6} style={{ marginBottom: '15px' }}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>

              <div className="auth-toggle">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    // Cancel MFA and go back to login
                    signOut();
                    setStep('login');
                    setMfaPendingUser(null);
                    setMfaFactorId(null);
                    setMfaChallengeId(null);
                    setMfaCode('');
                    setAuthError('');
                  }}
                >
                  Back to login
                </button>
              </div>
            </form>
          )}

          {/* Forgot Password Form */}
          {!isPasswordReset && authMode === 'forgot-password' && (
            <form onSubmit={otpResetStage === 'request' ? handleForgotPassword : handleOtpPasswordReset} className="login-form">
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2D5074' }}>
                {isAccountSetup ? 'Set Your Password' : 'Reset Password'}
              </h2>

              {authError && (
                <div className="auth-message error">{authError}</div>
              )}
              {authSuccess && (
                <div className="auth-message success">{authSuccess}</div>
              )}

              {otpResetStage === 'request' ? (
                <>
                  <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '20px' }}>
                    {isAccountSetup
                      ? 'Enter the email address you booked with and we’ll send you a code to set your password.'
                      : "Enter your email address and we'll email you how to reset your password."}
                  </p>

                  <input
                    type="email"
                    placeholder="Email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                    required
                  />

                  <button type="submit" disabled={loading}>
                    {loading ? 'Sending...' : isAccountSetup ? 'Send Code' : 'Send Reset Code'}
                  </button>

                  <div className="auth-toggle" style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        // A code from an earlier email still works (until it expires or a newer one is sent).
                        setOtpResetEmail(authForm.email.trim().toLowerCase());
                        setOtpResetStage('verify');
                        setAuthError('');
                        setAuthSuccess('');
                      }}
                    >
                      I already have a code
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '20px' }}>
                    Enter the code from the most recent reset email, then set a new password. If the email
                    has only a link, open the link instead — it takes you straight to setting a new password.
                  </p>

                  <input
                    type="email"
                    placeholder="Email"
                    value={otpResetEmail}
                    onChange={(e) => setOtpResetEmail(e.target.value.trim().toLowerCase())}
                    required
                  />

                  <input
                    type="text"
                    placeholder="Reset Code"
                    value={otpResetCode}
                    onChange={(e) => setOtpResetCode(e.target.value.trim())}
                    required
                  />

                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="New Password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex="-1"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>

                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm New Password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex="-1"
                    >
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>

                  {newPassword && confirmNewPassword && newPassword !== confirmNewPassword && (
                    <div className="password-match-error">Passwords do not match</div>
                  )}

                  <button type="submit" disabled={loading}>
                    {loading ? 'Updating...' : 'Verify Code & Update Password'}
                  </button>

                  <div className="auth-toggle" style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setOtpResetStage('request');
                        setAuthForm({ ...authForm, email: otpResetEmail });
                        setOtpResetCode('');
                        setNewPassword('');
                        setConfirmNewPassword('');
                        setAuthError('');
                        setAuthSuccess('');
                      }}
                    >
                      Send another email
                    </button>
                  </div>
                </>
              )}

              <div className="auth-toggle">
                Remember your password?{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setAuthMode('login');
                    setAuthError('');
                    setAuthSuccess('');
                    setOtpResetStage('request');
                    setOtpResetEmail('');
                    setOtpResetCode('');
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                >
                  Login
                </button>
              </div>
            </form>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  const handleOpenDataSecurityModal = () => setShowDataSecurityModal(true);
  const handleCloseDataSecurityModal = () => setShowDataSecurityModal(false);
  const handleGoHome = () => {
    setStep('dashboard');
  };


  // Booked a Goal-Setting Meeting but not a plan client yet: nothing to connect.
  if (isMeetingStageUser(user)) {
    return <MeetingStageHome name={client?.name} onLogout={handleLogout} />;
  }

  // Plaid connecting step is handled in the dashboard

  // Dashboard Screen
  return (
    <div className="app">
      {/* Inactivity Warning Modal */}
      {showInactivityWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px'
            }}>⏰</div>
            <h2 style={{
              margin: '0 0 15px 0',
              color: '#d32f2f',
              fontSize: '24px'
            }}>Session Timeout Warning</h2>
            <p style={{
              margin: '0 0 25px 0',
              color: '#666',
              fontSize: '16px',
              lineHeight: '1.5'
            }}>
              You've been inactive for 14 minutes. You will be automatically logged out in <strong>1 minute</strong> for security.
            </p>
            <button
              onClick={() => {
                setShowInactivityWarning(false);
                // Trigger activity to reset timer
                window.dispatchEvent(new Event('mousedown'));
              }}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
      <header className="header" style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          display: 'flex',
          gap: '10px'
        }}>
          <button
            onClick={handleGoHome}
            style={{
              padding: '10px 20px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              fontSize: 'clamp(14px, 2vw, 16px)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#2D5074';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.color = 'white';
            }}
          >
            Home
          </button>
          {/* Advisor only: send agreements and documents for signature */}
          {['advisor', 'admin'].includes(user?.app_metadata?.role) && (
            <a
              href="/send"
              style={{
                padding: '10px 20px',
                background: 'white',
                color: '#2D5074',
                border: '2px solid white',
                borderRadius: '8px',
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                textDecoration: 'none',
              }}
            >
              Documents
            </a>
          )}
          {/* Logout button in top-right corner */}
          <button
            onClick={handleLogout}
            style={{
              padding: '10px 20px',
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '8px',
              fontSize: 'clamp(14px, 2vw, 16px)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'white';
              e.target.style.color = '#2D5074';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.color = 'white';
            }}
          >
            Logout
          </button>
        </div>
        
        {/* Centered title and welcome message */}
        <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto', padding: '0 80px' }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Connected Accounts Dashboard</h1>
          <p style={{ margin: '0', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Welcome, {client?.name}</p>
          <div style={{
            marginTop: '8px',
            display: 'inline-flex',
            gap: '10px',
            alignItems: 'center',
            padding: '6px 12px',
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.15)',
            fontSize: 'clamp(12px, 2vw, 13px)'
          }}>
            Connected accounts: {connectedAccounts}
            <span style={{ opacity: 0.7 }}>·</span>
            Institutions: {connectedInstitutions}
          </div>
        </div>
      </header>

      <div className="dashboard">
        <div className="actions">
          <button onClick={refreshData} disabled={loading}>
            {loading ? 'Updating...' : 'Refresh Account Balances'}
          </button>

          <button onClick={() => handleConnectAccount()} disabled={plaidLinkLoading}>
            {plaidLinkLoading ? 'Connecting...' : connectedAccounts > 0 ? 'Connect another bank' : 'Connect account'}
          </button>

          {billingCanPortal && (
            <button type="button" onClick={handleOpenBillingPortal}>
              Invoices and subscription
            </button>
          )}
        </div>

        {plaidLinkError && (
          <div className="auth-message error" style={{ marginTop: '12px' }}>
            {plaidLinkError}
          </div>
        )}

        {(client?.clientId || user?.id) && (
          <SmsPreferences clientId={client?.clientId || user?.id} />
        )}

        {(client?.clientId || user?.id) && (
          <JobMatches clientId={client?.clientId || user?.id} />
        )}

        {/* MFA Setup Section */}
        {showMfaSetup && (
          <div className="mfa-setup-container">
            <h3>Set Up Two-Factor Authentication</h3>
            <p>Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.):</p>

            {authError && (
              <div className="auth-message error" style={{ marginBottom: '15px' }}>{authError}</div>
            )}

            <div className="mfa-qr-section">
              {mfaQrCode && (
                <img
                  src={mfaQrCode}
                  alt="MFA QR Code"
                  className="mfa-qr-code"
                />
              )}

              {mfaSecret && (
                <div className="mfa-secret">
                  <p style={{ fontSize: '0.85em', color: '#666', marginBottom: '5px' }}>
                    Can't scan? Enter this code manually:
                  </p>
                  <code className="mfa-secret-code">{mfaSecret}</code>
                </div>
              )}
            </div>

            <form onSubmit={handleMfaEnrollVerify} className="mfa-verify-form">
              <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                Enter the 6-digit code from your app to confirm:
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  setMfaCode(val);
                }}
                autoFocus
                style={{
                  textAlign: 'center',
                  fontSize: '24px',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  maxWidth: '250px',
                  margin: '0 auto 15px auto',
                  display: 'block'
                }}
                required
              />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={loading || mfaCode.length !== 6}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  {loading ? 'Verifying...' : 'Enable 2FA'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMfaSetup(false);
                    setMfaEnrolling(false);
                    setMfaQrCode(null);
                    setMfaSecret(null);
                    setMfaCode('');
                    setMfaFactorId(null);
                    setAuthError('');
                    // Unenroll the pending factor since user cancelled
                    if (mfaFactorId) {
                      unenrollMFA(mfaFactorId).catch(() => {});
                    }
                  }}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Two-Factor Authentication Toggle */}
        {!showMfaSetup && (
          <div className="mfa-toggle-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <span style={{ fontWeight: '600', fontSize: '15px' }}>Two-Factor Authentication</span>
                <span style={{
                  marginLeft: '10px',
                  padding: '3px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  backgroundColor: mfaEnabled ? '#d4edda' : '#f8d7da',
                  color: mfaEnabled ? '#155724' : '#721c24'
                }}>
                  {mfaEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {mfaEnabled ? (
                <button
                  onClick={handleMfaDisable}
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}
                >
                  {loading ? 'Disabling...' : 'Disable 2FA'}
                </button>
              ) : (
                <button
                  onClick={handleMfaEnroll}
                  disabled={mfaEnrolling}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}
                >
                  {mfaEnrolling ? 'Setting up...' : 'Enable 2FA'}
                </button>
              )}
            </div>
            {authSuccess && (
              <div className="auth-message success" style={{ marginTop: '10px' }}>{authSuccess}</div>
            )}
            {authError && (
              <div className="auth-message error" style={{ marginTop: '10px' }}>{authError}</div>
            )}
          </div>
        )}

        {!connectedAccountsLoaded ? (
          <div className="no-data">
            <h3>Loading Connected Accounts</h3>
            <p>Loading your connected institutions…</p>
          </div>
        ) : (
          <div className="connected-accounts-card">
            <div className="connected-accounts-card-header">
              <div>
                <h2>Connected accounts</h2>
                <p>
                  Review which institutions are connected and the balances currently available for reporting.
                </p>
              </div>
              <div className="connected-accounts-card-actions">
                {connectedAccountData.institutions.length > 0 && (
                  <button
                    type="button"
                    className="connected-accounts-add-cta"
                    onClick={() => handleConnectAccount()}
                    disabled={plaidLinkLoading}
                  >
                    {plaidLinkLoading ? 'Opening secure connection...' : 'Connect another bank'}
                  </button>
                )}
                {connectedAccountData.lastUpdated && (
                  <span>
                    Updated {formatLastUpdated(connectedAccountData.lastUpdated)}
                    {balancesRefreshing && ' · Refreshing balances…'}
                  </span>
                )}
                {!connectedAccountData.lastUpdated && balancesRefreshing && (
                  <span>Refreshing balances…</span>
                )}
              </div>
            </div>

            {institutionsNeedingLogin.length > 0 && (
              <div className="connected-accounts-reconnect-banner" role="alert">
                <div>
                  <strong>Bank sign-in required</strong>
                  <p>
                    {institutionsNeedingLogin.length === 1
                      ? `${institutionsNeedingLogin[0].institutionName} needs you to sign in again so balances and transactions stay up to date.`
                      : `${institutionsNeedingLogin.length} institutions need you to sign in again so balances and transactions stay up to date.`}
                  </p>
                </div>
                <button
                  type="button"
                  className="connected-accounts-reconnect-banner-cta"
                  onClick={() => handleConnectAccount(institutionsNeedingLogin[0].itemId)}
                  disabled={plaidLinkLoading || !institutionsNeedingLogin[0].itemId}
                >
                  {plaidLinkLoading ? 'Opening secure connection...' : 'Reconnect now'}
                </button>
              </div>
            )}

            {institutionsWithExpiringConsent.length > 0 && (
              <div className="connected-accounts-reconnect-banner" role="alert">
                <div>
                  <strong>Bank access expires soon</strong>
                  <p>
                    {institutionsWithExpiringConsent.length === 1
                      ? `${institutionsWithExpiringConsent[0].institutionName} needs access renewed${institutionsWithExpiringConsent[0].consentExpirationTime ? ` before ${new Date(institutionsWithExpiringConsent[0].consentExpirationTime).toLocaleDateString()}` : ' soon'} so balances and transactions keep updating.`
                      : `${institutionsWithExpiringConsent.length} institutions need access renewed soon so balances and transactions keep updating.`}
                  </p>
                </div>
                <button
                  type="button"
                  className="connected-accounts-reconnect-banner-cta"
                  onClick={() => handleConnectAccount(institutionsWithExpiringConsent[0].itemId)}
                  disabled={plaidLinkLoading || !institutionsWithExpiringConsent[0].itemId}
                >
                  {plaidLinkLoading ? 'Opening secure connection...' : 'Renew access'}
                </button>
              </div>
            )}

            {institutionsNeedingInvestmentsConsent.length > 0 && (
              <div className="connected-accounts-holdings-banner" role="status">
                <div>
                  <strong>Investment holdings access needed</strong>
                  <p>
                    {institutionsNeedingInvestmentsConsent.length === 1
                      ? `${institutionsNeedingInvestmentsConsent[0].institutionName} is connected for balances, but investment holdings (shares and tickers) need one more approval.`
                      : `These institutions need investment holdings approval: ${institutionsNeedingInvestmentsConsent
                          .map((inst) => inst.institutionName)
                          .join(', ')}.`}{' '}
                    Use <strong>Update access</strong> on each row below — do not connect the same bank again.
                  </p>
                </div>
                <button
                  type="button"
                  className="connected-accounts-holdings-banner-cta"
                  onClick={() =>
                    handleConnectAccount(institutionsNeedingInvestmentsConsent[0].itemId)
                  }
                  disabled={plaidLinkLoading || !institutionsNeedingInvestmentsConsent[0].itemId}
                >
                  {plaidLinkLoading ? 'Opening secure connection...' : 'Update access'}
                </button>
              </div>
            )}

            {connectedAccountData.institutions.length > 0 &&
              institutionsNeedingInvestmentsConsent.length === 0 && (
              <p className="connected-accounts-holdings-note">
                Brokerage holdings appear in planning reports after investment data access is approved
                for each investment institution.
              </p>
            )}

            {connectedAccountData.institutions.length === 0 ? (
              <div className="no-data connected-accounts-empty">
                <h3>No accounts connected yet</h3>
                <p>Start here to securely link the accounts you want included in your planning reports.</p>
                <button
                  type="button"
                  className="connected-accounts-empty-cta"
                  onClick={() => handleConnectAccount()}
                  disabled={plaidLinkLoading}
                >
                  {plaidLinkLoading ? 'Opening secure connection...' : 'Connect account'}
                </button>
              </div>
            ) : (
              <>
                <div className="connected-table-wrap">
                  <table className="connected-table">
                    <thead>
                      <tr>
                        <th>Institution</th>
                        <th>Connection Status</th>
                        <th>Action Needed</th>
                        <th>Manage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connectedAccountData.institutions.map((institution) => (
                        <tr key={institution.itemId || institution.institutionName}>
                          <td>{institution.institutionName}</td>
                          <td>
                            <span className={`connection-badge ${institution.connectionStatus === 'Connected' ? 'connected' : 'needs-action'}`}>
                              {institution.connectionStatus}
                            </span>
                          </td>
                          <td>{institution.actionNeeded || '-'}</td>
                          <td>
                            {institution.itemId ? (
                              <button
                                type="button"
                                className={
                                  institution.needsReconnect || institution.connectionStatus === 'Action needed'
                                    ? 'connected-accounts-reconnect-cta'
                                    : 'connected-accounts-add-cta'
                                }
                                onClick={() => handleConnectAccount(institution.itemId)}
                                disabled={plaidLinkLoading}
                              >
                                {institution.needsReconnect || institution.connectionStatus === 'Action needed'
                                  ? institution.needsInvestmentsConsent
                                    ? 'Update access'
                                    : 'Reconnect'
                                  : 'Update access'}
                              </button>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <section className="connected-account-detail-section">
                  <h3>Connected accounts by link</h3>
                  <p>
                    Each row is one account on one Plaid link. Use account type, last four digits,
                    and balance to spot duplicate connections.
                  </p>
                  <div className="connected-table-wrap">
                    <table className="connected-table connected-table-detail">
                      <thead>
                        <tr>
                          <th>Institution</th>
                          <th>Account name</th>
                          <th>Type</th>
                          <th>Last 4</th>
                          <th>Balance</th>
                          <th>Connection</th>
                        </tr>
                      </thead>
                      <tbody>
                        {connectedAccountData.accounts.map((account, index) => (
                          <tr key={`${account.institutionName}-${account.accountMask}-${account.accountName}-${index}`}>
                            <td>{account.institutionName}</td>
                            <td>{account.accountName}</td>
                            <td>{formatAccountType(account.accountType, account.accountSubtype)}</td>
                            <td>{account.accountMask || '-'}</td>
                            <td className="connected-balance">{formatCurrency(account.balance)}</td>
                            <td>{account.connectionStatus}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="connected-accounts-note">
                    Your connected institutions will continue to refresh in future reports when they are generated.
                  </p>
                </section>
              </>
            )}
          </div>
        )}


        <DataSecurityModal isOpen={showDataSecurityModal} onClose={handleCloseDataSecurityModal} />
      </div>
      <Footer onOpenDataSecurityModal={handleOpenDataSecurityModal} />
    </div>
  );
}

export default App;
